import _ from 'lodash';
import { DataFeedSubscription } from '@tradingblock/types';
import { useLog } from '@tradingblock/components';
import { getCashieringUpdateSubscriptionCmd, getOrderUpdateSubscription } from './DataFeed';
import { SymbolChannels } from '../../types';
import { webSocketService } from './WebSocketService';
import { newsWebSocketService } from './NewsWebSocketService';
import { orderStatusWebSocketService } from './OrderStatusWebSocketService';
import { expiringTokensWebSocketService } from './ExpiringTokensWebSocketService';
import { cashieringWebSocketService } from './CashieringWebSocketService';
import { quotesWebSocketService } from './QuotesWebSocketService';
import { spreadsWebSocketService } from './SpreadsWebSocketService';
import { useFeatureFlag } from '@tradingblock/components';
import { useStateSelector, isMarketOpenSelector } from '../global/dataSelectors';
import { useQuotesFeedStatus, useSpreadsFeedStatus, useFeedStatus } from './FeedSelectors';
import { 
  getSubscriptionStrategy, 
  SubscriptionContext, 
} from './SubscriptionStrategies';

// Feature flag controlled symbol extraction
const getSymbols = (sub: DataFeedSubscription | DataFeedSubscription[]) => {
  const subscription = _.isArray(sub) ? sub : [sub];
  return _.flatMap(subscription, s => (s.kind === 'Quote' ? s.symbols : []));
};

interface orderUpdateRequest {
  type: string;
  accountId?: number;
  accountIds?: number[];
}

export function useFeedActions(apiToken: string = '') {
  const log = useLog('DataFeed');
  const {isEnabled: useLofiOrderUpdates} = useFeatureFlag('lofi-order-updates');
  const {isEnabled: useLofiCashiering} = useFeatureFlag('lofi-cashiering');
    
  // Feature flags for feed actions - separate toggles for quote and spread services
  const { isEnabled: useNewQuoteService } = useFeatureFlag('new_quote_service');
  const { isEnabled: useNewSpreadService } = useFeatureFlag('new_spread_service');
  
  // Get websocket availability status
  const quotesFeedStatus = useQuotesFeedStatus();
  const spreadsFeedStatus = useSpreadsFeedStatus();
  const oldFeedStatus = useFeedStatus();
  
  // Determine which websocket to use based on availability
  // Use new websocket if enabled and open, otherwise fall back to old websocket
  const shouldUseNewQuoteService = useNewQuoteService && quotesFeedStatus === 'open';
  const shouldUseNewSpreadService = useNewSpreadService && spreadsFeedStatus === 'open';
  
  // Get market state from Redux (more accurate than utility function)
  const marketOpenState = useStateSelector(isMarketOpenSelector);
  const isMarketCurrentlyOpen = marketOpenState !== undefined ? marketOpenState : false;

  const trySend = (cmd: string | undefined) => {
    try {
      if (cmd) {
        webSocketService.sendMessage(cmd);
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send old websocket cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendQuotes = (cmd: string | {type: string, symbols: string[] | any[]} | undefined) => {
    try {
      if (cmd) {
        quotesWebSocketService.sendMessage(JSON.stringify(cmd));
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send quotes cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendSpreads = (cmd: string | {type: string, spreads: string[] | any[]} | undefined) => {
    try {
      if (cmd) {
        spreadsWebSocketService.sendMessage(JSON.stringify(cmd));
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send spreads cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendNews = (cmd: string | undefined) => {
    try {
      if (cmd) {
        newsWebSocketService.sendMessage(cmd);
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send news cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendOrderStatus = (cmd: string | undefined) => {
    try {
      if (cmd) {
        orderStatusWebSocketService.sendMessage(cmd);
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send order status cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendCashieringCmd = (cmd: string | undefined) => {
    try {
      if (cmd) {
        cashieringWebSocketService.sendMessage(cmd);
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send cashiering cmd "', cmd, '": ', ex);
    }
    return false;
  };
  const trySendExpiringTokensCmd = (cmd: string | undefined) => {
    try {
      if (cmd) {
        expiringTokensWebSocketService.sendMessage(cmd);
        return true;
      }
    } catch (ex) {
      console.warn('useFeedActions :: Caught error trying to send expiring tokens cmd "', cmd, '": ', ex);
      // throw ex;
    }
    return false;
  };
  const subscribeQuote = (accountId: string | number, sub: DataFeedSubscription | DataFeedSubscription[]): boolean => {
      const subscription = _.isArray(sub) ? sub : [sub];
      
      // Check if subscription is empty before proceeding
      const hasSpreads = subscription.some(s => s.kind === 'Spread' && s.spreadDefinition && s.spreadDefinition.length > 0);
      const hasQuotes = subscription.some(s => s.kind === 'Quote' && s.symbols && s.symbols.length > 0);
      
      // If no spreads and no quotes, skip subscription (don't trigger empty subscription)
      if (!hasSpreads && !hasQuotes) {
        return true; // Return true to indicate "success" (no-op, nothing to subscribe)
      }
      
      // Create subscription context
      const context: SubscriptionContext = {
        apiToken,
        trySend,
        trySendQuotes,
        trySendSpreads,
        isMarketCurrentlyOpen
      };
      
      // Get the appropriate strategy based on subscription type and websocket availability
      // Route to new websocket if available, otherwise fall back to old websocket
      const strategy = getSubscriptionStrategy(
        subscription, 
        context, 
        shouldUseNewQuoteService, 
        shouldUseNewSpreadService
      );
      
      // Execute subscription
      const result = strategy.subscribe(accountId, subscription);
      
      log.extend('subscribe')(`%o`, { 
        symbols: getSymbols(sub), 
        strategy: strategy.name,
        success: result.success,
        error: result.error,
        websocketAvailability: {
          quotesFeedStatus,
          spreadsFeedStatus,
          oldFeedStatus,
          shouldUseNewQuoteService,
          shouldUseNewSpreadService
        }
      });
      
      if (!result.success) {
        console.warn(`Subscription failed using ${strategy.name}:`, result.error);
      }
      
      return result.success;
    }
  const unsubscribeQuote = 
    (accountId: string | number, sub: DataFeedSubscription | DataFeedSubscription[]) => {
      const subscription = _.isArray(sub) ? sub : [sub];
      
      // Check if subscription is empty before proceeding
      const hasSpreads = subscription.some(s => s.kind === 'Spread' && s.spreadDefinition && s.spreadDefinition.length > 0);
      const hasQuotes = subscription.some(s => s.kind === 'Quote' && s.symbols && s.symbols.length > 0);
      
      // If no spreads and no quotes, skip unsubscription (don't trigger empty unsubscription)
      if (!hasSpreads && !hasQuotes) {
        return true; // Return true to indicate "success" (no-op, nothing to unsubscribe)
      }
      
      // Create subscription context
      const context: SubscriptionContext = {
        apiToken,
        trySend,
        trySendQuotes,
        trySendSpreads,
        isMarketCurrentlyOpen
      };
      
      // Get the appropriate strategy based on subscription type and websocket availability
      // Route to new websocket if available, otherwise fall back to old websocket
      const strategy = getSubscriptionStrategy(
        subscription, 
        context, 
        shouldUseNewQuoteService, 
        shouldUseNewSpreadService
      );
      
      // Execute unsubscription
      const result = strategy.unsubscribe(accountId, subscription);
      
      // Log the result
      log.extend('unsubscribe')(`%o`, { 
        symbols: getSymbols(sub), 
        strategy: strategy.name,
        success: result.success,
        error: result.error,
        websocketAvailability: {
          quotesFeedStatus,
          spreadsFeedStatus,
          oldFeedStatus,
          shouldUseNewQuoteService,
          shouldUseNewSpreadService
        }
      });
      
      if (!result.success) {
        console.warn(`Unsubscription failed using ${strategy.name}:`, result.error);
      }
      
      return result.success;
    }
  const subscribeOrderUpdates = 
    (accountId: string | number) => {
      if (!useLofiOrderUpdates) {
        const cmd = getOrderUpdateSubscription(apiToken, accountId);
        return trySend(cmd);
      } else {
        const parsedAccountId: number = typeof accountId === 'string' ? parseInt(accountId) : accountId;
        const request = {
          type: 'Subscribe',
          accountId: parsedAccountId,
        };
        const cmd = JSON.stringify(request);
        return trySendOrderStatus(cmd);
      }
    }
  const unsubscribeOrderUpdates = 
    (accountId: string | number) => {
      if (!useLofiOrderUpdates) {
        const cmd = getOrderUpdateSubscription(apiToken, accountId, true);
        return trySend(cmd);
      } else {
        const parsedAccountId: number = typeof accountId === 'string' ? parseInt(accountId) : accountId;
        const request: orderUpdateRequest = {
          type: 'Unsubscribe',
          accountId: parsedAccountId,
        };
        const cmd = JSON.stringify(request);
        return trySendOrderStatus(cmd);
      }
    }
  const subscribeExpiringTokens = 
    () => {
      const cmd = JSON.stringify({
      type: 'Subscribe',
    });
    return trySendExpiringTokensCmd(cmd);
  }
  const unsubscribeExpiringTokens = () => {
    const cmd = JSON.stringify({
      type: 'Unsubscribe',
    });
    return trySendExpiringTokensCmd(cmd);
  }
  const subscribeToCashieringUpdates = 
    (accountIds: (string | number)[]) => {
      if (!useLofiCashiering) { 
        return trySend(getCashieringUpdateSubscriptionCmd(apiToken, accountIds));
      } else {
        if (accountIds.length === 0) { return false; }
        const requestAccountIdsArr: number[] = [];
          accountIds.forEach(id => {
            const parsedAccountId: number = typeof id === 'string' ? parseInt(id) : id;
            requestAccountIdsArr.push(parsedAccountId);
          });

        const request = {
          type: 'Subscribe',
          accountIds: requestAccountIdsArr,
        };
        const cmd = JSON.stringify(request);
        return trySendCashieringCmd(cmd);
      }
    }
  const unsubscribeFromCashieringUpdates = 
    (accountIds: (string | number)[]) => {
      if (!useLofiCashiering) { 
        return trySend(getCashieringUpdateSubscriptionCmd(apiToken, accountIds, true));
      } else {
        const requestAccountIdsArr: number[] = [];
        accountIds.forEach(id => {
          const parsedAccountId: number = typeof id === 'string' ? parseInt(id) : id;
          requestAccountIdsArr.push(parsedAccountId);
        });

        const request = {
          type: 'Unsubscribe',
          accountIds: requestAccountIdsArr,
        };
        const cmd = JSON.stringify(request);
        return trySendCashieringCmd(cmd);
      }
    }

    const sendNewsSymbolsRequest = (symbols: SymbolChannels[]) => {
      const cmd = JSON.stringify(symbols);
      return trySendNews(cmd);
    }

  return {
    subscribeQuote,
    unsubscribeQuote,
    subscribeOrderUpdates,
    unsubscribeOrderUpdates,
    subscribeExpiringTokens,
    unsubscribeExpiringTokens,
    unsubscribeFromCashieringUpdates,
    subscribeToCashieringUpdates,
    sendNewsSymbolsRequest,
  };
}
